import * as ort from "onnxruntime-web";

const MEL_BINS = 32;
const EMBED_DIM = 96;
const WINDOW_SIZE = 76;
const STEP_SIZE = 8;
export const CHUNK = 1280; // 80 ms @ 16 kHz

/**
 * Streaming melspectrogram + speech-embedding pipeline.
 * Faithful port of openWakeWord's AudioFeatures.
 */
export class AudioFeatures {
  private melspecSession: ort.InferenceSession;
  private embeddingSession: ort.InferenceSession;
  private sampleRate: number;
  private melspecInputName: string;
  private embeddingInputName: string;
  private rawDataMaxLen: number;
  private melspectrogramMaxLen: number;
  private featureBufferMaxLen: number;

  private rawDataBuffer: number[] = [];
  private rawDataRemainder = new Int16Array(0);
  private accumulatedSamples = 0;
  private melBuffer: Float32Array[] = [];
  featureBuffer: Float32Array[] = [];

  constructor(
    melspecSession: ort.InferenceSession,
    embeddingSession: ort.InferenceSession,
    { sampleRate = 16000 }: { sampleRate?: number } = {},
  ) {
    this.melspecSession = melspecSession;
    this.embeddingSession = embeddingSession;
    this.sampleRate = sampleRate;
    this.melspecInputName = melspecSession.inputNames[0];
    this.embeddingInputName = embeddingSession.inputNames[0];
    this.rawDataMaxLen = sampleRate * 10;
    this.melspectrogramMaxLen = 10 * 97;
    this.featureBufferMaxLen = 120;
    this.reset(true);
  }

  reset(skipWarmup = false) {
    this.rawDataBuffer = [];
    this.rawDataRemainder = new Int16Array(0);
    this.accumulatedSamples = 0;
    this.melBuffer = [];
    for (let i = 0; i < WINDOW_SIZE; i++) {
      this.melBuffer.push(new Float32Array(MEL_BINS).fill(1));
    }
    this.featureBuffer = [];
    void skipWarmup;
  }

  async warmup() {
    const audio = new Int16Array(this.sampleRate * 4);
    for (let i = 0; i < audio.length; i++) {
      audio[i] = Math.floor(Math.random() * 2000 - 1000);
    }
    this.featureBuffer = await this.getEmbeddings(audio);
  }

  private async getMelspectrogram(int16: Int16Array): Promise<Float32Array[]> {
    // INTENTIONAL (not an openWakeWord bug): divide by 32768 so PCM matches [-1, 1].
    // Our ONNX wake models are trained via livekit-wakeword (sf.read float clips +
    // inference/model.py:117 int16/32768). Stock openWakeWord keeps int16-range floats;
    // passing that here collapses live room-distance detection (~8/8 → ~1/8). Do not
    // "fix" this back to Float32Array.from(int16) without re-validating our models.
    const x = Float32Array.from(int16, (v) => v / 32768);
    const tensor = new ort.Tensor("float32", x, [1, x.length]);
    const out = await this.melspecSession.run({
      [this.melspecInputName]: tensor,
    });
    const o = out[this.melspecSession.outputNames[0]];
    const dims = o.dims;
    const bins = Number(dims[dims.length - 1]);
    const frames = Number(dims[dims.length - 2]);
    const data = o.data as Float32Array;
    const rows: Float32Array[] = [];
    for (let f = 0; f < frames; f++) {
      const row = new Float32Array(bins);
      const base = f * bins;
      for (let b = 0; b < bins; b++) {
        row[b] = data[base + b] / 10 + 2;
      }
      rows.push(row);
    }
    return rows;
  }

  private async embedWindows(
    windows: Float32Array[][],
  ): Promise<Float32Array[]> {
    const n = windows.length;
    if (n === 0) return [];
    const data = new Float32Array(n * WINDOW_SIZE * MEL_BINS);
    let p = 0;
    for (const win of windows) {
      for (let r = 0; r < WINDOW_SIZE; r++) {
        data.set(win[r], p);
        p += MEL_BINS;
      }
    }
    const tensor = new ort.Tensor("float32", data, [
      n,
      WINDOW_SIZE,
      MEL_BINS,
      1,
    ]);
    const out = await this.embeddingSession.run({
      [this.embeddingInputName]: tensor,
    });
    const flat = out[this.embeddingSession.outputNames[0]].data as Float32Array;
    const rows: Float32Array[] = [];
    for (let i = 0; i < n; i++) {
      rows.push(flat.slice(i * EMBED_DIM, i * EMBED_DIM + EMBED_DIM));
    }
    return rows;
  }

  private async getEmbeddings(int16: Int16Array): Promise<Float32Array[]> {
    const spec = await this.getMelspectrogram(int16);
    const windows: Float32Array[][] = [];
    for (let i = 0; i < spec.length; i += STEP_SIZE) {
      const window = spec.slice(i, i + WINDOW_SIZE);
      if (window.length === WINDOW_SIZE) windows.push(window);
    }
    return this.embedWindows(windows);
  }

  private bufferRawData(int16: Int16Array) {
    for (let i = 0; i < int16.length; i++) this.rawDataBuffer.push(int16[i]);
    if (this.rawDataBuffer.length > this.rawDataMaxLen) {
      this.rawDataBuffer = this.rawDataBuffer.slice(-this.rawDataMaxLen);
    }
  }

  private async streamingMelspectrogram(nSamples: number) {
    if (this.rawDataBuffer.length < 400) {
      throw new Error(
        "The number of input frames must be at least 400 samples @ 16khz (25 ms)!",
      );
    }
    const start = Math.max(0, this.rawDataBuffer.length - (nSamples + 160 * 3));
    const tail = Int16Array.from(this.rawDataBuffer.slice(start));
    const rows = await this.getMelspectrogram(tail);
    for (const row of rows) this.melBuffer.push(row);
    if (this.melBuffer.length > this.melspectrogramMaxLen) {
      this.melBuffer = this.melBuffer.slice(-this.melspectrogramMaxLen);
    }
  }

  async streamingFeatures(x: Int16Array): Promise<number> {
    let processedSamples = 0;

    if (this.rawDataRemainder.length !== 0) {
      const merged = new Int16Array(this.rawDataRemainder.length + x.length);
      merged.set(this.rawDataRemainder, 0);
      merged.set(x, this.rawDataRemainder.length);
      x = merged;
      this.rawDataRemainder = new Int16Array(0);
    }

    if (this.accumulatedSamples + x.length >= CHUNK) {
      const remainder = (this.accumulatedSamples + x.length) % CHUNK;
      if (remainder !== 0) {
        const xEven = x.subarray(0, x.length - remainder);
        this.bufferRawData(xEven);
        this.accumulatedSamples += xEven.length;
        this.rawDataRemainder = x.slice(x.length - remainder);
      } else {
        this.bufferRawData(x);
        this.accumulatedSamples += x.length;
        this.rawDataRemainder = new Int16Array(0);
      }
    } else {
      this.accumulatedSamples += x.length;
      this.bufferRawData(x);
    }

    if (
      this.accumulatedSamples >= CHUNK &&
      this.accumulatedSamples % CHUNK === 0
    ) {
      await this.streamingMelspectrogram(this.accumulatedSamples);

      for (let i = this.accumulatedSamples / CHUNK - 1; i >= 0; i--) {
        const ndx = -STEP_SIZE * i === 0 ? this.melBuffer.length : -STEP_SIZE * i;
        const endAbs = ndx < 0 ? this.melBuffer.length + ndx : ndx;
        const startAbs = endAbs - WINDOW_SIZE;
        if (startAbs >= 0) {
          const window = this.melBuffer.slice(startAbs, endAbs);
          const [embedding] = await this.embedWindows([window]);
          this.featureBuffer.push(embedding);
        }
      }

      processedSamples = this.accumulatedSamples;
      this.accumulatedSamples = 0;
    }

    if (this.featureBuffer.length > this.featureBufferMaxLen) {
      this.featureBuffer = this.featureBuffer.slice(-this.featureBufferMaxLen);
    }

    return processedSamples !== 0 ? processedSamples : this.accumulatedSamples;
  }

  getFeatures(nFeatureFrames = 16, startNdx = -1) {
    let frames: Float32Array[];
    if (startNdx !== -1) {
      const end =
        startNdx + nFeatureFrames === 0
          ? undefined
          : startNdx + nFeatureFrames;
      frames = this.featureBuffer.slice(startNdx, end);
    } else {
      frames = this.featureBuffer.slice(-nFeatureFrames);
    }
    const n = frames.length;
    const data = new Float32Array(n * EMBED_DIM);
    for (let i = 0; i < n; i++) data.set(frames[i], i * EMBED_DIM);
    return { data, dims: [1, n, EMBED_DIM] as number[] };
  }
}
